import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import DOMPurify from 'dompurify';
import { Bubble } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import {
  activateTipInGroup,
  registerTipInGroup,
} from './tipGroupRegistry';
import type { IInfoIconTipIconType, IInfoIconTipProps } from './types';
import './index.scss';

const SELECTED_ICON: IInfoIconTipIconType = {
  default: 'tab-tip-icon',
  hover: 'action-tab-icon',
};

const DEFAULT_ICON: IInfoIconTipIconType = {
  default: 'alert-circle-m',
  hover: 'alert-circle-action',
};

const resolveInfoIconType = (
  isHovered: boolean,
  selected: boolean,
  customIconType?: IInfoIconTipIconType,
): string => {
  const preset = customIconType ?? (selected ? SELECTED_ICON : DEFAULT_ICON);
  return isHovered ? preset.hover : preset.default;
};

const buildInfoIconTipContent = (
  tipText: IInfoIconTipProps['tipText'],
): React.ReactNode | null => {
  if (!tipText) {
    return null;
  }
  if (typeof tipText === 'string') {
    const html = DOMPurify.sanitize(tipText.replace(/(\\n|\n)/g, '<br/>'));
    return (
      <div
        className="dbi-info-icon-tip__content"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  return <div className="dbi-info-icon-tip__content">{tipText}</div>;
};

/**
 * 情报模块通用 info icon 气泡。
 * 受控 Bubble（visible + trigger="empty"），避免默认 hover 导致 sticky。
 * 同组 groupId 互斥，避免快速滑过多个 icon 时气泡叠加。
 */
const InfoIconTip: React.FC<IInfoIconTipProps> = ({
  tipText,
  selected = false,
  iconType,
  placement = 'top',
  maxWidth,
  placementOffset = 10,
  className,
  overlayClassName,
  groupId,
  onClick,
}) => {
  const [visible, setVisible] = useState(false);
  const [isIconHover, setIsIconHover] = useState(false);
  const instanceIdRef = useRef(
    `info-tip-${Math.random().toString(36).slice(2, 10)}`,
  );

  const closeTip = useCallback(() => {
    setIsIconHover(false);
    setVisible(false);
  }, []);

  useEffect(() => {
    if (!groupId) {
      return undefined;
    }
    return registerTipInGroup(groupId, instanceIdRef.current, closeTip);
  }, [groupId, closeTip]);

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      if (groupId) {
        activateTipInGroup(groupId, instanceIdRef.current);
      }
      setIsIconHover(true);
      setVisible(true);
    },
    [groupId],
  );

  const handleMouseLeave = useCallback((e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    setIsIconHover(false);
    setVisible(false);
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      onClick?.(e);
    },
    [onClick],
  );

  const handleVisibleChange = useCallback(
    (nextVisible: boolean) => {
      setVisible(nextVisible);
      if (!nextVisible) {
        setIsIconHover(false);
      }
    },
    [],
  );

  useEffect(() => {
    closeTip();
  }, [selected, closeTip]);

  const content = useMemo(() => buildInfoIconTipContent(tipText), [tipText]);

  if (!content) {
    return null;
  }

  const resolvedIconType = resolveInfoIconType(isIconHover, selected, iconType);

  return (
    <span
      className={classnames(
        'dbi-info-icon-tip',
        'db-tipcontext-icon',
        className,
      )}
    >
      <Bubble
        dark
        trigger="empty"
        visible={visible}
        destroyOnClose
        placement={placement}
        placementOffset={placementOffset}
        style={maxWidth ? { maxWidth } : undefined}
        content={content}
        className={classnames('dbi-info-icon-tip-bubble', overlayClassName)}
        onVisibleChange={handleVisibleChange}
      >
        <span
          className="db-tipcontext-box"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
        >
          <SvgIcon type={resolvedIconType} />
        </span>
      </Bubble>
    </span>
  );
};

export { closeTipGroup } from './tipGroupRegistry';
export type { IInfoIconTipProps } from './types';
export default InfoIconTip;
